import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Avatar, Button, FormCard, FormItem, PageHeader } from '@lmc/ui-kit'
import { privacyOfficersConstants } from 'locale/privacyOfficers'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Form, Select } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import { useRoleAccess } from 'components/AccessControl'
import { Carer, Home, User, UserRole } from '@lmc/ts-common'
import { useRouter } from 'hooks/useRouter'
import { useDispatch, useSelector } from 'react-redux'
import Operations from 'operations'
import useCarers, { getCarer } from 'hooks/useFindItem/useCarers'
import dayjs from 'dayjs'

type Props = {}

export const PrivacyOfficersScreen: FC<Props> = () => {
  const { t } = useTranslation(NAMESPACE.PRIVACY_OFFICERS)

  const [isEditMode, setIsEditMode] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const { navigate } = useRouter()

  const dispatch = useDispatch()

  const user = useSelector(
    (state: { global: { user: User } }) => state.global?.user
  )

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const hasRolePermission = useRoleAccess(UserRole.careHomeAdmin)

  const { all: carers } = useCarers({})

  const carerOptions = carers
    ?.filter(
      (carer) =>
        !carer?.deleted &&
        !(carer as Carer & { hidden?: boolean })?.hidden &&
        carer.active &&
        carer.role === UserRole.careHomeAdmin
    )
    .map((carer) => ({
      value: carer?.id,
      label: `${carer?.name?.first} ${carer?.name?.last}`,
    }))

  useEffect(() => {
    if (home) {
      form.setFieldsValue({
        privacyOfficers: home?.privacyOfficers?.map((officer) => officer?.user),
      })
    }
  }, [home])

  useEffect(() => {
    if (!hasRolePermission) {
      navigate(`../billing`, { relative: 'path' })
    }
  }, [hasRolePermission])

  const handleEdit = () => setIsEditMode((prev) => !prev)

  const handleOnFinish = (values: { privacyOfficers?: string[] }) => {
    setIsLoading(true)
    const res = dispatch(
      Operations.saveHomeDetails({
        privacyOfficers: values.privacyOfficers,
        id: user?.home,
      })
    )
    if (res) {
      setIsLoading(false)
      setIsEditMode(false)
    }
  }

  const [form] = Form.useForm()

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <Container>
      <PageHeader
        title={t(
          privacyOfficersConstants[
            isEditMode ? 'EDITING_PRIVACY_OFFICERS' : 'PRIVACY_OFFICERS'
          ]
        )}
        subtitle={
          !isEditMode ? t(privacyOfficersConstants.BELOW_LISTS) : undefined
        }
      >
        <ButtonWrapper>
          {!isEditMode && (
            <Button variant="primary" onClick={handleEdit}>
              {t(privacyOfficersConstants.EDIT)}
            </Button>
          )}
          {isEditMode && (
            <>
              <Button onClick={handleEdit}>
                {t(privacyOfficersConstants.CANCEL)}
              </Button>
              <Button
                variant="primary"
                onClick={form.submit}
                isLoading={isLoading}
              >
                {t(privacyOfficersConstants.SAVE)}
              </Button>
            </>
          )}
        </ButtonWrapper>
      </PageHeader>
      {isEditMode && (
        <CardWrapper>
          <Form form={form} onFinish={handleOnFinish}>
            <FormCard>
              <FormItem
                label={
                  <Text>{t(privacyOfficersConstants.PRIVACY_OFFICER)}</Text>
                }
                name="privacyOfficers"
                rules={[
                  {
                    required: true,
                    message: t(privacyOfficersConstants.YOU_MUST),
                  },
                ]}
                extra={<>{t(privacyOfficersConstants.THESE_USERS)}</>}
              >
                <Select
                  options={carerOptions}
                  mode="multiple"
                  filterOption={filterOption}
                />
              </FormItem>
            </FormCard>
          </Form>
        </CardWrapper>
      )}
      {!isEditMode && !isLoading && (
        <ListWrapper>
          {home?.privacyOfficers?.map((officer) => {
            const carer = getCarer(carers, officer.user)

            return (
              <OfficerListItem>
                <Avatar url={carer?.picture as unknown as string} />
                <Text weight="bold">{`${carer?.name?.first} ${carer?.name?.last}`}</Text>
                <SinceText weight="regular">
                  {`${t(privacyOfficersConstants.SINCE)}
                  ${dayjs(officer?.startDate).format('DD-MM-YYYY')}
                  `}
                </SinceText>
              </OfficerListItem>
            )
          })}
        </ListWrapper>
      )}
    </Container>
  )
}

const Container = styled.div`
  padding-top: 24px;
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    padding-left: 24px;
    padding-right: 24px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  margin-left: auto;
  gap: 8px;
`

const SinceText = styled(Text)`
  margin-left: auto;
`

const CardWrapper = styled.div`
  margin-top: 24px;
`

const ListWrapper = styled.div`
  margin-top: 24px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const OfficerListItem = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
  padding: 16px 28px;
  display: flex;
  align-items: center;
  gap: 16px;
`
